import {styled} from '@linaria/react';
import React, {CSSProperties, FC} from 'react';
import {SPACING} from '../../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../constants/typography';
import {useGetAriaLabelFromUrl} from '../../../../hooks/useGetAriaLabelFromUrl/useGetAriaLabelFromUrl';
import {Image} from '../../../atoms/Image/Image';
import {Text} from '../../../atoms/Text/Text';
import {UnstyledLink} from '../../../atoms/UnstyledLink/UnstyledLink';
import {ProductImage} from '../../../molecules/ProductImage/ProductImage';
import {SPOTLIGHT_GLOWS_MOBILE} from '../glows';
import {SpotlightSlideBackground} from './SpotlightSlideBackground';
import {SpotlightSlideBaseProps} from './types';

export type SpotlightSlideAmbientProps = SpotlightSlideBaseProps;

/**
 * Mobile Spotlight slide shell. Deliberately mirrors the shared `SlideAmbient`
 * layout (the shipped, "already working" arrangement) so it only swaps the card
 * *background* — everything else keeps its proven position:
 *   - the badge pill floats, centered, just above the card top edge;
 *   - the sponsored slot floats top-left, the info icon floats top-right;
 *   - the card content (claim + optional subline, or a centered brand logo) and
 *     the product packshots anchor to the card bottom;
 *   - the topline + headline sit centered *below* the card.
 *
 * Glow lives on ambient `SpotlightSlideAmbientBackground` (full-bleed so blooms
 * can overflow the tile). The card always paints its solid base colour, and
 * adds media (+ scrim) when present — matching the desktop panel so colour-only
 * slides still read as a filled card. Colour-only slides additionally paint
 * `glowColors` blooms on the card itself (same slot set as the ambient, so the
 * two roughly overlap like Figma). Text colors are backend-driven
 * (`cardContent.color` on the card, `foregroundColor` below it).
 */
export const SpotlightSlideAmbient: FC<SpotlightSlideAmbientProps> = ({
  backgroundColor,
  glowColors,
  media,
  cardContent,
  headline,
  topline,
  foregroundColor,
  productImages,
  badge,
  infoIcon,
  sponsored,
  bannerTarget,
  isActive,
  isCloseToViewport,
  onSlideClick,
  onStopAutomaticSlide,
  className,
  'data-testid': dataTestid = 'spotlightSlideAmbient',
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  const textContent = cardContent?.kind === 'text' ? cardContent : undefined;
  const logoContent = cardContent?.kind === 'logo' ? cardContent : undefined;
  const hasProducts = Boolean(productImages && productImages.length > 0);

  return (
    <CardContainer
      className={className}
      data-testid={dataTestid}
      data-active={isActive}
      to={bannerTarget.url}
      aria-label={getAriaLabelFromUrl(bannerTarget.url)}
      onClick={e => {
        e.preventDefault();
        bannerTarget.onClick?.();
        onSlideClick?.(e);
      }}
    >
      <Card>
        {sponsored && (
          <FloatingTopLeft data-testid="spotlight-sponsored">
            {sponsored}
          </FloatingTopLeft>
        )}

        {infoIcon && (
          <FloatingTopRight
            data-testid="spotlight-info-icon"
            style={
              textContent
                ? ({color: textContent.color} as CSSProperties)
                : undefined
            }
          >
            {infoIcon}
          </FloatingTopRight>
        )}

        <ImageContainer onTouchStart={onStopAutomaticSlide}>
          <CardSurface>
            <SpotlightSlideBackground
              backgroundColor={backgroundColor}
              glows={SPOTLIGHT_GLOWS_MOBILE}
              glowColors={glowColors}
              media={media}
              mediaLoading={isCloseToViewport ? 'eager' : 'lazy'}
            />
            <ContentWrapper>
              {logoContent && (
                <LogoWrapper data-testid="spotlight-card-logo">
                  <LogoImage
                    src={logoContent.image.url}
                    alt={logoContent.image.description}
                    loading="lazy"
                    breakpoints={[]}
                    ratio="2:1"
                    widths={[300]}
                    disableFixedRatio
                  />
                </LogoWrapper>
              )}
              {(textContent || hasProducts) && (
                <SlideContentWrapper>
                  {textContent && (
                    <ClaimWrapper
                      style={{color: textContent.color} as CSSProperties}
                    >
                      <ClaimLabel data-testid="spotlight-card-text">
                        {textContent.headline}
                      </ClaimLabel>
                      {textContent.subline && (
                        <ClaimSubline data-testid="spotlight-card-subline">
                          {textContent.subline}
                        </ClaimSubline>
                      )}
                    </ClaimWrapper>
                  )}
                  {hasProducts && (
                    <ProductsWrapper>
                      {productImages?.map((product, index) => (
                        <StyledProductImage
                          src={product.url}
                          widths={[120, 240, 360]}
                          key={`spotlight-slide-product-${index}`}
                          alt={product.description}
                          increasePadding
                          padded
                        />
                      ))}
                    </ProductsWrapper>
                  )}
                </SlideContentWrapper>
              )}
            </ContentWrapper>
          </CardSurface>

          {badge && (
            <TopChipWrapper data-testid="spotlight-badge">
              {badge}
            </TopChipWrapper>
          )}

          {bannerTarget.url && <BannerTarget />}
        </ImageContainer>

        {(topline || headline) && (
          <HeadlinesWrapper style={{color: foregroundColor} as CSSProperties}>
            {topline && <Topline>{topline}</Topline>}
            {headline && (
              <Headline data-testid="spotlight-headline">{headline}</Headline>
            )}
          </HeadlinesWrapper>
        )}
      </Card>
    </CardContainer>
  );
};

const CardContainer = styled(UnstyledLink)`
  display: block;
  height: 100%;
  position: relative;
  padding-top: 13px;
  list-style: none;
`;

const Card = styled.div`
  position: relative;
  z-index: 1;

  @keyframes cover-flow {
    0%,
    100% {
      transform: scaleY(0.85);
    }
    50% {
      transform: scaleY(1);
    }
  }

  @supports (view-timeline-axis: inline) {
    --timeline-name: --cover;
    view-timeline: var(--timeline-name);
    will-change: transform, opacity;
    view-timeline-axis: inline;
    animation: cover-flow both linear;
    animation-timeline: var(--timeline-name);
  }

  @supports not (view-timeline-axis: inline) {
    transform: scaleY(0.85);
    opacity: 0.4;
    transition: transform 0.3s ease-out, opacity 0.3s ease-out;
    will-change: transform;

    [data-active='true'] & {
      transform: scaleY(1);
      opacity: 1;
    }
  }
`;

const FloatingTopLeft = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
`;

const FloatingTopRight = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const CardSurface = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0.75px 4.5px 6px 0 rgba(0, 0, 0, 0.08),
    0 1.5px 12px 6px rgba(0, 0, 0, 0.07);
`;

const TopChipWrapper = styled.div`
  position: absolute;
  top: -11px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 2;
`;

const BannerTarget = styled.div`
  position: absolute;
  inset: -11px 0 0 0;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const LogoWrapper = styled.div`
  z-index: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${SPACING['X.Md']};
`;

const LogoImage = styled(Image)`
  height: 50px;
  max-width: 162px;
  width: 100%;
  object-fit: contain;
`;

const SlideContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClaimWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 0 24px;
  margin-bottom: 24px;
  color: inherit;
`;

const ClaimLabel = styled(Text)`
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.17;
  text-align: center;
  color: inherit;
`;

const ClaimSubline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w400};
  line-height: 1.42;
  text-align: center;
  color: inherit;
`;

const ProductsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 8px 8px;
  gap: 2px;
`;

const StyledProductImage = styled(ProductImage)`
  flex: 1 1 0;
  min-width: 0;
  height: auto;
  aspect-ratio: 3 / 4;
  padding-top: 0;

  &&& img {
    padding: 14% 12%;
  }
`;

const HeadlinesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  margin-top: 8px;
  color: inherit;
`;

const Topline = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.5;
  text-transform: uppercase;
  text-align: center;
  color: inherit;
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w400};
  line-height: 1.42;
  text-align: center;
  color: inherit;
`;
